<template>
  <div class="dropzone uploaddocuments uploaddocuments--receipts"
    :class="isFailed()
      ? 'dropzone--error'
      : ''
    ">
    <div class="dropzone__content">
      <div v-if="isInitial()" class="text">
        <p>{{ dropzoneStrings.drag_documents }}</p>
      </div>
      <div v-if="isSaving()" class="text">
        <p>{{ dropzoneStrings.is_saving }}</p>
      </div>
      <div v-if="isFailed()" class="text">
        <p>{{ dropzoneStrings.upload_error }}</p>
        <p> {{ uploadError }} </p>
        <p>{{ dropzoneStrings.try_again }}</p>
        <a class="container-link" @click.prevent="reset()"></a>
      </div>
      <LennocModal
        v-if="isReviewing()"
        :domclass="'dropzone--idle dropzone--gradient'" >

        <div class="dropzone__file-info">
          <div class="dropzone__title">{{ dropzoneStrings.receipt_upload_title }}</div>
          <div class="table table--upload">
            <div class="row" v-for="item in fileList" :key="item.file.name">
              <div class="cell cell--description">
                <span class="cell--header">{{ dropzoneStrings.receipt_description }}</span>
                <LennocMultiSelect
                  :id="`description__${item.id}`"
                  :items="getDescriptionList()"
                  :placeholder="dropzoneStrings.receipt_description"
                  :closeDropdownButtonText="dropzoneStrings.document_type_closedropdownbuttontext"
                  :closeDropdownOnCheckboxClick="closeDropdownOnCheckboxClick"
                  @input="processDescriptionInput($event, item.id)"
                  @blur="processConvertCurrency()" />
              </div>
              <div class="cell cell--currency">
                <span class="cell--header">{{ dropzoneStrings.receipt_currency }}</span>
                <LennocSelect
                  :id="`currency__${item.id}`"
                  :items="getCurrencyList()"
                  :placeholder="dropzoneStrings.receipt_currency"
                  @input="processCurrencyInput($event, item.id)"
                  @blur="processConvertCurrency()"
                  :staticLabel="true" />
              </div>
              <div class="cell cell--amount">
                <div class="cell--header">{{ dropzoneStrings.receipt_amount }}</div>
                <LennocInput
                  label=""
                  placeholder="..."
                  :id="`amount__${item.id}`"
                  @change="processAmountInput($event, item.id)"
                  @blur="processConvertCurrency()"
                  :staticLabel="true" />
              </div>
              <div class="cell cell--country-currency">
                <div class="cell--header">{{ dropzoneStrings.receipt_country_currency }}</div>
                <LennocInput
                  label=""
                  :id="`country_currency__${item.id}`"
                  :placeholder="getAmount(item)"
                  disabled />
              </div>
              <div class="cell cell--file cell--link">
                <div class="cell--header">{{ dropzoneStrings.file_name_title }}</div>
                <span v-if="isImage(item)" class="value value--link" @click="viewFile(item)">
                <LennocIcon image="eye" />
                  {{ item.file.name }}
                </span>
                <span class="value" v-else>{{ item.file.name }}</span>
                <LennocFileView
                  v-if="viewerData"
                  :document="viewerData"
                  :popUpStrings="popUpStrings"
                  @close="viewerData = null" />
              </div>
              <div class="cell">
                <div class="cell--header">{{ dropzoneStrings.delete_title }}</div>
                <a class="link link--edit link--action" :title="deleteTitle(item.file.name)" @click="removeFile(item.file.name)">
                  <LennocIcon image="squared-cross" />
                </a>
              </div>
            </div>
          </div>
          <div class="text--small">{{ dropzoneStrings.receipt_currency_rate_disclaimer }}</div>
          <div class="text">{{ dropzoneStrings.receipt_upload_text }}</div>
          <div class="dropzone__controls">
            <div class="hitzone"
              @mouseenter="showToolTip = true"
              @mouseleave="showToolTip = false">
              <button
                class="button button--sec"
                :disabled="!isReady()"
                @click.prevent="startUpload()"
              >
                <span>{{ dropzoneStrings.upload_button }}</span>
                <LennocToolTip v-if="!isReady() && showToolTip" :error="true">{{ fileMessage }}</LennocToolTip>
              </button>
            </div>
          </div>

        </div>

      </LennocModal>
    </div>
    <form v-show="isInitial() || isFailed()" ref="uploadForm">
      <input
        type="file"
        multiple
        name="filesToUpload"
        ref="fileControl"
        :disabled="isSaving()"
        @change="filesChange($event)"
        class="input-file" />
    </form>
  </div>
</template>
<script lang="ts" setup>
import { onMounted, computed, ref, watch, PropType } from 'vue'
import { FileObject, MultiSelectEvent, SelectEvent } from '../../types/types'

const STATUS_INITIAL = 0
const STATUS_REVIEW = 1
const STATUS_SAVING = 2
const STATUS_FAILED = 3

const props = defineProps({
  claimId: {
    type: String,
    default: ''
  },
  questionData: {
    type: Object,
    required: true
  },
  documentId: {
    type: String,
    default: ''
  },
  dropzoneStrings: {
    type: Object,
    required: true
  },
  popUpStrings: {
    type: Object,
    required: true
  },
  secret: {
    type: String,
    default: ''
  },
  document: {
    type: Object,
    default: {}
  },
  environment: {
    type: String,
    required: true
  },
  closeDropdownOnCheckboxClick: {
    type: Boolean,
    default: false
  },
  supportedFileTypes: {
    type: Array as PropType<string[]>,
    default: []
  },
  uploadError: {
    type: String,
    default: ''
  }
  ,
  convertCurrencyCallback: {
    type: Function,
    default: null
  }
})

const emits = defineEmits([
  'convertCurrency',
  'download',
  'save'
])


onMounted(() => {
    reset()
  })

  watch(() => props.uploadError, () => {
    if (props.uploadError) {
      setError(props.uploadError)
    }
  })

const amount = ref('')
const amountInEuros = ref('')
const uploadError = ref()
const currentStatus = ref()
const fileList = ref<Array<FileObject>>([])
const busy = ref(false)
const viewerData = ref()
const showToolTip = ref(false)
const uploadForm = ref()
const error = ref(false)

// Computed properties

const fileCount = () => {
  return fileList.value.length
}

const fileMessage = () => {
  return props.dropzoneStrings.value.receipt_no_fields_entered
}

const isInitial = () => {
  return currentStatus.value === STATUS_INITIAL
}

const isReviewing = () => {
  return currentStatus.value === STATUS_REVIEW
}

const isSaving = () => {
  return currentStatus.value === STATUS_SAVING
}

const isFailed = () => {
  return currentStatus.value === STATUS_FAILED
}

const getIdleClass = () => {
  return !isInitial() ? 'dropzone--idle dropzone--gradient' : ''
}

const isReady = () => {
  let ready = false
  fileList.value.forEach(f => {
    if ((f.Description?.length ?? 0) > 0 && f.Amount && f.Currency) {
      ready = true
    } else {
      ready = false
    }
  })
  return ready
}

const supportedFileTypesLowerCase = computed(() => {
      return props.supportedFileTypes.map(supportedFileType => supportedFileType.toLowerCase())
  })

// Methods

const addFile = (fileObj: File, index: number) => {
  const newFile = {
    id: `droppedFile_${index}`, //   // To do: investigate why 2 versions of this property exists
    Id: `droppedFile_${index}`,
    file: fileObj,
    kind: [],
    name: fileObj.name,
    Name: fileObj.name,
    FileName : fileObj.name
  }

  fileList.value.push(newFile)
}

const convertCurrency = async (payload: FileObject, id: string) => {
  const item = fileList.value.find(f => f.id === id)

  if(item == null)
    return

  payload.Amount = payload.Amount?.replace(',', '.')
  item.AmountInEuros = await props.convertCurrencyCallback(payload)
}

const deleteTitle = (fileName: string) => {
  return props.dropzoneStrings.delete_file_hover_text.replace('*fileName*', fileName)
}

const getAmount = (data: FileObject) => {
  const item = fileList.value.find(f => f.id === data.id)
  return item?.AmountInEuros ? item.AmountInEuros : ''
}

const getDescriptionList = () => {
  return Object.entries(props.dropzoneStrings.receipt_description_list).map(i => {
    return { key: i[0], label: props.dropzoneStrings.receipt_description_list[i[0]] }
  })
}

const getCurrencyList = () => {
  return Object.entries(props.dropzoneStrings.receipt_currency_list).map(i => {
    return { Code: i[0], label: props.dropzoneStrings.receipt_currency_list[i[0]] }
  })
}

const filesChange = (event: Event) => {
  const unsupported = []
  const filesizeTooLarge = []
  const target = event.target as HTMLInputElement
  const files = target.files as FileList

  Array.from(files).forEach((f, i) => {
    if (f.size > 9000000) {
      filesizeTooLarge.push(f.name)
    }
    if (!isFileSupported(f.name)) {
      unsupported.push(f.name)
    }
    if (!filesizeTooLarge.length && !unsupported.length) {
      addFile(f, i)
    }
  })
  if (filesizeTooLarge.length) {
    setError(props.dropzoneStrings.file_too_large)
  } else if (unsupported.length) {
    setError(props.dropzoneStrings.file_type_not_allowed)
  } else {
    currentStatus.value = STATUS_REVIEW
  }
}

const isFileSupported = (fileName: string) => {
  if (props.supportedFileTypes === null || props.supportedFileTypes === undefined) {
    return false
  }
  const extension = fileName.substr(fileName.lastIndexOf('.')).toLowerCase()
  return supportedFileTypesLowerCase.value.indexOf(extension) !== -1
}

const isImage = (fileObj: FileObject) => {
  return fileObj.file.type.indexOf('image') === 0 ||
      fileObj.file.type.indexOf('pdf') > -1
}

const processConvertCurrency = () => {
  fileList.value.forEach((f, index) => {
        if (f.Amount && f.Currency) {
          const payload = {
            id: f.id,
            name: f.name,
            kind: f.kind,
            file: f.file,
            Amount: f.Amount,
            Currency: f.Currency
          }
          convertCurrency(payload, f.id)
        }
      })
}

const processDescriptionInput = (event: MultiSelectEvent, fileId: string) => {
  const item = fileList.value.find(f => f.id === fileId)
  
  if(!item)
    return null;

  item.Description = event.value
  processConvertCurrency()
}

const processCurrencyInput = (event: SelectEvent, fileId: string) => {
  const item = fileList.value.find(f => f.id === fileId)
  
  if(!item)
    return null;

  item.Currency = event.Code;
  processConvertCurrency()
}

const processAmountInput = (event: Event, fileId: string) => {
  const item = fileList.value.find(f => f.id === fileId)
  
  if(!item)
    return null;

  const target = event.target as HTMLInputElement
  item.Amount = target.value
  processConvertCurrency()
}

const removeFile = (fileName: string) => {
  fileList.value = fileList.value.filter(f => f.file.name !== fileName)
      if (fileList.value.length === 0) {
        reset()
      }
}

const reset = () => {
  busy.value = false
  uploadForm.value.reset()
  currentStatus.value = STATUS_INITIAL
  uploadError.value = null
  fileList.value = []
  showToolTip.value = false
  error.value = false
}

const setError = (message: string) => {
  busy.value = false
  currentStatus.value = STATUS_FAILED
  uploadError.value = message

  // Clear the selected file (list). Selecting a file that is already in the list, will not trigger a change if you try to upload the same file again, because the form thinks that you already selected it.
  uploadForm.value.reset()
  fileList.value = []
}

const startUpload = async () => {
  currentStatus.value = STATUS_SAVING
    if (props.environment === 'portal') {
      const formData = new FormData()
      formData.append('claimId', props.claimId)
      formData.append('parentDocumentId', props.documentId)
      let isActionPerformed = false;
      if( props.document !== null && props.document.DocumentActionPerformedDateTime !== null ) {
        isActionPerformed = true;
      }
      formData.append('isActionPerformed', isActionPerformed.toString())
      for (const[index, f] of fileList.value.entries()) {
        formData.append(`receiptAmount_${index}`, f.Amount ?? '')
          formData.append(`receiptCurrency_${index}`, f.Currency ?? '')
          formData.append(`uploadedReceipt_${index}`, f.file)
          formData.append(`receiptDescription_${index}`, f.Description?.join(',') ?? '')

          formData.append(`uploadedFile_${index}`, f.file)
        formData.append(`fileKind_${index}`, Object.keys(f.kind).join(','))
      }
      emits('save', formData)
    } else {
      const docId = props.questionData.AnswerSchema.Fields[0].UploadType
      const files = []
      for (const file of fileList.value) {
        files.push( {
          fileObject: file,
          type: docId,
          kind: Object.values(file.Description ?? '').join(', '),
          currency: file.Currency,
          amount: file.Amount,
          amountInEuros: file.AmountInEuros        
        })
      }
      emits('save', files)
    }
    reset()
}

const viewFile = (fileObj: FileObject) => {
  if (isImage(fileObj)) {
        viewerData.value = {
          file: fileObj.file
        }
      }
}

</script>